import { useCallback } from "react";
import { STORAGE_KEYS } from "../constants/storageKeys";
import { MAX_SESSIONS } from "../constants/previewConfig";
import {
  createSession,
  validateSessionsList,
  parseImportedSessionJson,
  serializeSession,
} from "../utils/sessionUtils";
import { useLocalStorage } from "./useLocalStorage";

export function usePreviewSessions() {
  const [sessions, setSessions] = useLocalStorage(
    STORAGE_KEYS.SESSIONS,
    [],
    Array.isArray,
    validateSessionsList
  );

  const saveSession = useCallback(
    (name, snapshot) => {
      const session = createSession({ name, ...snapshot });
      setSessions((prev) => [session, ...prev].slice(0, MAX_SESSIONS));
      return session;
    },
    [setSessions]
  );

  const renameSession = useCallback(
    (id, name) => {
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, name, updatedAt: new Date().toISOString() } : s))
      );
    },
    [setSessions]
  );

  const deleteSession = useCallback(
    (id) => {
      setSessions((prev) => prev.filter((s) => s.id !== id));
    },
    [setSessions]
  );

  const duplicateSession = useCallback(
    (id) => {
      setSessions((prev) => {
        const original = prev.find((s) => s.id === id);
        if (!original) return prev;
        const copy = createSession({ ...original, name: `${original.name} (copy)` });
        return [copy, ...prev].slice(0, MAX_SESSIONS);
      });
    },
    [setSessions]
  );

  const exportSession = useCallback(
    (id) => {
      const session = sessions.find((s) => s.id === id);
      return session ? serializeSession(session) : null;
    },
    [sessions]
  );

  const importSessionFromJson = useCallback(
    (text) => {
      const result = parseImportedSessionJson(text);
      if (!result.valid) return result;

      setSessions((prev) => [result.session, ...prev].slice(0, MAX_SESSIONS));
      return result;
    },
    [setSessions]
  );

  return {
    sessions,
    saveSession,
    renameSession,
    deleteSession,
    duplicateSession,
    exportSession,
    importSessionFromJson,
  };
}
